import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { usersAPI } from '../services/api';
import { formatDateTime } from '../utils/currency';
import { formatPhoneNumber } from '../utils/validation';
import { Plus, Search, Users, Edit, Trash2, Phone, Mail, Shield, Lock, Eye, EyeOff, DollarSign, Bell } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import PasswordVerificationDialog from '../components/PasswordVerificationDialog';

const StaffManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [passwordVerifyOpen, setPasswordVerifyOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'staff',
    hideFinancials: false,
    permissions: {
      canViewBookings: true,
      canEditBookings: true,
      canDeleteBookings: false,
      canExportBookings: false,
      canViewLeads: true,
      canEditLeads: true,
      canDeleteLeads: false,
      canExportLeads: false,
      canViewVenues: true,
      canEditVenues: false,
      canViewInventory: true,
      canEditInventory: false,
      canViewAccounts: false,
      canEditAccounts: false,
      canViewAccountHeads: false,
      canEditAccountHeads: false,
      canViewTransfers: false,
      canEditTransfers: false,
      canViewAccountReports: false,
      canViewReports: false,
      canExportBookingAnalytics: false,
      canExportFinancialAnalytics: false
    },
    notificationPreferences: {
      paymentReminders: true,
      newBookings: true,
      newLeads: true,
      bookingUpdates: true,
      leadUpdates: true
    }
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const teamMembers = await usersAPI.getTeamMembers();
      // Filter staff, managers, stakeholders, and receptionists
      const teamMembersList = teamMembers.filter(u => 
        u.role === 'staff' || u.role === 'manager' || u.role === 'stakeholder' || u.role === 'receptionist'
      );
      setUsers(teamMembersList);
    } catch (error) {
      console.error('Failed to load team members:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members. Please try again.',
        variant: 'destructive'
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast({
        title: 'Validation Error',
        description: 'Phone number must be exactly 10 digits',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate password match (for new staff only)
    if (!editingUser && formData.password !== formData.confirmPassword) {
      toast({
        title: 'Validation Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate password length (for new staff only)
    if (!editingUser && formData.password.length < 6) {
      toast({
        title: 'Validation Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Transform old permission format to new format
      const transformPermissions = (perms) => {
        return {
          bookings: [
            ...(perms.canViewBookings ? ['view'] : []),
            ...(perms.canEditBookings ? ['edit', 'create'] : []),
            ...(perms.canDeleteBookings ? ['delete'] : []),
            ...(perms.canExportBookings ? ['export'] : [])
          ],
          leads: [
            ...(perms.canViewLeads ? ['view'] : []),
            ...(perms.canEditLeads ? ['edit', 'create'] : []),
            ...(perms.canDeleteLeads ? ['delete'] : []),
            ...(perms.canExportLeads ? ['export'] : [])
          ],
          venues: [
            ...(perms.canViewVenues ? ['view'] : []),
            ...(perms.canEditVenues ? ['edit'] : [])
          ],
          inventory: [
            ...(perms.canViewInventory ? ['view'] : []),
            ...(perms.canEditInventory ? ['edit'] : [])
          ],
          // expenses permission mirrors accounts - UI groups them as "Accounts - Income/Expenses"
          expenses: [
            ...(perms.canViewAccounts ? ['view'] : []),
            ...(perms.canEditAccounts ? ['edit', 'create', 'delete', 'export'] : [])
          ],
          accounts: [
            ...(perms.canViewAccounts ? ['view'] : []),
            ...(perms.canEditAccounts ? ['edit', 'create', 'delete', 'export'] : [])
          ],
          accountHeads: [
            ...(perms.canViewAccountHeads ? ['view'] : []),
            ...(perms.canEditAccountHeads ? ['edit', 'create', 'delete'] : [])
          ],
          transfers: [
            ...(perms.canViewTransfers ? ['view'] : []),
            ...(perms.canEditTransfers ? ['edit', 'create', 'delete'] : [])
          ],
          accountReports: perms.canViewAccountReports ? ['view', 'export'] : [],
          invoices: [
            ...(perms.canViewInvoices ? ['view'] : []),
            ...(perms.canEditInvoices ? ['edit', 'create', 'delete', 'export'] : [])
          ],
          financials: perms.canViewReports ? ['view'] : [],
          reports: perms.canViewReports ? ['view', 'export'] : [],
          bookingAnalytics: perms.canExportBookingAnalytics ? ['export'] : [],
          financialAnalytics: perms.canExportFinancialAnalytics ? ['export'] : []
        };
      };

      const transformedPermissions = transformPermissions(formData.permissions);

      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          permissions: transformedPermissions,
          hideFinancials: formData.hideFinancials,
          notificationPreferences: formData.notificationPreferences
        };
        
        // Only include password if it was changed
        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }
        
        await usersAPI.updateUser(editingUser.id, updateData);
        toast({ title: 'Success', description: 'Staff member updated successfully' });
      } else {
        // Create new user via backend API
        const memberData = {
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          password: formData.password,
          role: formData.role,
          permissions: transformedPermissions,
          notificationPreferences: formData.notificationPreferences
        };
        
        await usersAPI.createTeamMember(memberData);
        toast({ title: 'Success', description: 'Staff member created successfully! They can now log in.' });
      }

      await loadUsers();
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save staff member:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save staff member. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    
    // Transform backend permission format to frontend format
    const transformPermissionsToFrontend = (perms) => {
      if (!perms) {
        return {
          canViewBookings: true,
          canEditBookings: true,
          canDeleteBookings: false,
          canExportBookings: false,
          canViewLeads: true,
          canEditLeads: true,
          canDeleteLeads: false,
          canExportLeads: false,
          canViewVenues: true,
          canEditVenues: false,
          canViewInventory: true,
          canEditInventory: false,
          canViewAccounts: false,
          canEditAccounts: false,
          canViewAccountHeads: false,
          canEditAccountHeads: false,
          canViewTransfers: false,
          canEditTransfers: false,
          canViewAccountReports: false,
          canViewReports: false,
          canExportBookingAnalytics: false,
          canExportFinancialAnalytics: false
        };
      }
      
      // Check if it's already in old format
      if ('canViewBookings' in perms) {
        return {
          ...perms,
          canExportBookings: perms.canExportBookings ?? false,
          canExportLeads: perms.canExportLeads ?? false,
          canViewAccounts: perms.canViewAccounts ?? false,
          canEditAccounts: perms.canEditAccounts ?? false,
          canViewAccountHeads: perms.canViewAccountHeads ?? false,
          canEditAccountHeads: perms.canEditAccountHeads ?? false,
          canViewTransfers: perms.canViewTransfers ?? false,
          canEditTransfers: perms.canEditTransfers ?? false,
          canViewAccountReports: perms.canViewAccountReports ?? false,
          canExportBookingAnalytics: perms.canExportBookingAnalytics ?? false,
          canExportFinancialAnalytics: perms.canExportFinancialAnalytics ?? false
        };
      }
      
      // Transform new format to old format
      return {
        canViewBookings: perms.bookings?.includes('view') ?? true,
        canEditBookings: perms.bookings?.includes('edit') ?? true,
        canDeleteBookings: perms.bookings?.includes('delete') ?? false,
        canExportBookings: perms.bookings?.includes('export') ?? false,
        canViewLeads: perms.leads?.includes('view') ?? true,
        canEditLeads: perms.leads?.includes('edit') ?? true,
        canDeleteLeads: perms.leads?.includes('delete') ?? false,
        canExportLeads: perms.leads?.includes('export') ?? false,
        canViewVenues: perms.venues?.includes('view') ?? true,
        canEditVenues: perms.venues?.includes('edit') ?? false,
        canViewInventory: perms.inventory?.includes('view') ?? true,
        canEditInventory: perms.inventory?.includes('edit') ?? false,
        canViewAccounts: perms.accounts?.includes('view') ?? false,
        canEditAccounts: perms.accounts?.includes('edit') ?? false,
        canViewAccountHeads: perms.accountHeads?.includes('view') ?? false,
        canEditAccountHeads: perms.accountHeads?.includes('edit') ?? false,
        canViewTransfers: perms.transfers?.includes('view') ?? false,
        canEditTransfers: perms.transfers?.includes('edit') ?? false,
        canViewAccountReports: perms.accountReports?.includes('view') ?? false,
        canViewInvoices: perms.invoices?.includes('view') ?? false,
        canEditInvoices: perms.invoices?.includes('edit') ?? false,
        canViewReports: perms.reports?.includes('view') ?? false,
        canExportBookingAnalytics: perms.bookingAnalytics?.includes('export') ?? false,
        canExportFinancialAnalytics: perms.financialAnalytics?.includes('export') ?? false
      };
    };

    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      password: '', // Don't show password for editing (can't retrieve hashed password)
      role: user.role || 'staff',
      hideFinancials: user.hideFinancials || false,
      permissions: transformPermissionsToFrontend(user.permissions),
      notificationPreferences: user.notificationPreferences || {
        paymentReminders: user.role !== 'staff', // Default: staff don't get payment reminders
        newBookings: true,
        newLeads: true,
        bookingUpdates: true,
        leadUpdates: true
      }
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    // Close confirmation dialog and open password verification
    setDeleteConfirmOpen(false);
    setPasswordVerifyOpen(true);
  };

  const handlePasswordVerify = async (password) => {
    // For deletion, we verify by trying to login with current user's credentials
    // Then proceed with the delete via API
    try {
      if (userToDelete) {
        // Delete via backend API
        await usersAPI.deleteUser(userToDelete.id);
        
        await loadUsers();
        toast({ 
          title: 'Success', 
          description: `${userToDelete.role === 'manager' ? 'Manager' : 'Staff member'} deleted successfully` 
        });
        setUserToDelete(null);
        return { success: true };
      }
      return { success: false, message: 'User not found' };
    } catch (error) {
      console.error('Failed to delete user:', error);
      return { success: false, message: error.message || 'Failed to delete user. Please try again.' };
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'staff',
      hideFinancials: false,
      permissions: {
        canViewBookings: true,
        canEditBookings: true,
        canDeleteBookings: false,
        canExportBookings: false,
        canViewLeads: true,
        canEditLeads: true,
        canDeleteLeads: false,
        canExportLeads: false,
        canViewVenues: true,
        canEditVenues: false,
        canViewInventory: true,
        canEditInventory: false,
        canViewReports: false,
        canExportBookingAnalytics: false,
        canExportFinancialAnalytics: false
      },
      notificationPreferences: {
        paymentReminders: true,
        newBookings: true,
        newLeads: true,
        bookingUpdates: true,
        leadUpdates: true
      }
    });
    setEditingUser(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const togglePermission = (key) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: !formData.permissions[key]
      }
    });
  };

  const toggleNotificationPreference = (key) => {
    setFormData({
      ...formData,
      notificationPreferences: {
        ...formData.notificationPreferences,
        [key]: !formData.notificationPreferences[key]
      }
    });
  };

  const handleToggleStaffFinancials = async (user) => {
    try {
      await usersAPI.updateUser(user.id, { 
        hideFinancials: !user.hideFinancials 
      });
      
      await loadUsers();
      
      toast({ 
        title: 'Updated', 
        description: `Financial visibility ${user.hideFinancials ? 'enabled' : 'disabled'} for ${user.name}` 
      });
    } catch (error) {
      console.error('Failed to update financial visibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      manager: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 border-orange-200',
      staff: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200',
      stakeholder: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 border-purple-200',
      receptionist: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 border-teal-200'
    };
    return colors[role] || colors.staff;
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground">Staff Management</h2>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-0.5">Manage team members and access</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 shadow-md w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Add Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full sm:max-w-3xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update staff member information and permissions' : 'Create a new staff member with custom permissions'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Information */}
              <div className="space-y-4 w-full">
                <h3 className="font-semibold text-gray-900 dark:text-foreground flex items-center">
                  <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select value={formData.role} onValueChange={(value) => {
                      // When selecting receptionist, automatically set restricted permissions
                      if (value === 'receptionist') {
                        setFormData({
                          ...formData, 
                          role: value,
                          hideFinancials: true, // Receptionist always has financials hidden
                          permissions: {
                            canViewBookings: false,
                            canEditBookings: false,
                            canDeleteBookings: false,
                            canExportBookings: false,
                            canViewLeads: false,
                            canEditLeads: false,
                            canDeleteLeads: false,
                            canExportLeads: false,
                            canViewVenues: false,
                            canEditVenues: false,
                            canViewInventory: false,
                            canEditInventory: false,
                            canViewReports: false,
                            canExportBookingAnalytics: false,
                            canExportFinancialAnalytics: false,
                            canViewCalendar: true  // Only calendar access
                          }
                        });
                      } else if (value === 'staff') {
                        // Staff always has financials hidden
                        setFormData({...formData, role: value, hideFinancials: true});
                      } else {
                        // Manager and Stakeholder can have financials visibility controlled
                        setFormData({...formData, role: value});
                      }
                    }}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="stakeholder">Stakeholder</SelectItem>
                        <SelectItem value="receptionist">Receptionist (Calendar Only)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.role === 'receptionist' && (
                      <p className="text-xs text-teal-600 mt-1">
                        Receptionist can only view the Calendar page
                      </p>
                    )}
                    {(formData.role === 'staff' || formData.role === 'receptionist') && (
                      <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                        Note: Financial data is always hidden for this role
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone * (10 digits, no spaces)</Label>
                    <Input 
                      id="phone" 
                      type="tel" 
                      placeholder="9876543210"
                      value={formData.phone} 
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setFormData({...formData, phone: digitsOnly});
                      }} 
                      pattern="[0-9]{10}"
                      maxLength="10"
                      title="Please enter exactly 10 digits without spaces"
                      required
                      className={formData.phone && formData.phone.length !== 10 ? 'border-red-500' : ''}
                    />
                    {formData.phone && formData.phone.length > 0 && formData.phone.length !== 10 && (
                      <p className="text-xs text-red-500">⚠️ Must be exactly 10 digits ({formData.phone.length}/10)</p>
                    )}
                  </div>
                </div>
                
                {/* Password Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Password {editingUser ? '(leave blank to keep current)' : '*'}
                    </Label>
                    <div className="relative">
                      <Input 
                        id="password" 
                        type={showPassword ? "text" : "password"}
                        value={formData.password} 
                        onChange={(e) => setFormData({...formData, password: e.target.value})} 
                        required={!editingUser}
                        placeholder={editingUser ? '••••••••' : 'Enter password'}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {!editingUser && (
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <div className="relative">
                        <Input 
                          id="confirmPassword" 
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword} 
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} 
                          required={!editingUser}
                          placeholder="Re-enter password"
                          className={`pr-10 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-500' : ''}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                        <p className="text-xs text-red-500">⚠️ Passwords do not match</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Access Permissions */}
              <div className="space-y-4 border-t pt-4 w-full">
                <h3 className="font-semibold text-gray-900 dark:text-foreground flex items-center">
                  <Shield className="w-4 h-4 mr-2 flex-shrink-0" />
                  Access Permissions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bookings */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Bookings</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewBookings" 
                          checked={formData.permissions.canViewBookings}
                          onCheckedChange={() => togglePermission('canViewBookings')}
                        />
                        <label htmlFor="canViewBookings" className="text-sm">View Bookings</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditBookings" 
                          checked={formData.permissions.canEditBookings}
                          onCheckedChange={() => togglePermission('canEditBookings')}
                        />
                        <label htmlFor="canEditBookings" className="text-sm">Edit Bookings</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canDeleteBookings" 
                          checked={formData.permissions.canDeleteBookings}
                          onCheckedChange={() => togglePermission('canDeleteBookings')}
                        />
                        <label htmlFor="canDeleteBookings" className="text-sm">Delete Bookings</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canExportBookings" 
                          checked={formData.permissions.canExportBookings}
                          onCheckedChange={() => togglePermission('canExportBookings')}
                        />
                        <label htmlFor="canExportBookings" className="text-sm">Download Bookings Report</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Leads */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Leads</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewLeads" 
                          checked={formData.permissions.canViewLeads}
                          onCheckedChange={() => togglePermission('canViewLeads')}
                        />
                        <label htmlFor="canViewLeads" className="text-sm">View Leads</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditLeads" 
                          checked={formData.permissions.canEditLeads}
                          onCheckedChange={() => togglePermission('canEditLeads')}
                        />
                        <label htmlFor="canEditLeads" className="text-sm">Edit Leads</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canDeleteLeads" 
                          checked={formData.permissions.canDeleteLeads}
                          onCheckedChange={() => togglePermission('canDeleteLeads')}
                        />
                        <label htmlFor="canDeleteLeads" className="text-sm">Delete Leads</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canExportLeads" 
                          checked={formData.permissions.canExportLeads}
                          onCheckedChange={() => togglePermission('canExportLeads')}
                        />
                        <label htmlFor="canExportLeads" className="text-sm">Download Leads Report</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Venues */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Venues</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewVenues" 
                          checked={formData.permissions.canViewVenues}
                          onCheckedChange={() => togglePermission('canViewVenues')}
                        />
                        <label htmlFor="canViewVenues" className="text-sm">View Venues</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditVenues" 
                          checked={formData.permissions.canEditVenues}
                          onCheckedChange={() => togglePermission('canEditVenues')}
                        />
                        <label htmlFor="canEditVenues" className="text-sm">Edit Venues</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Others */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Other Access</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewInventory" 
                          checked={formData.permissions.canViewInventory}
                          onCheckedChange={() => togglePermission('canViewInventory')}
                        />
                        <label htmlFor="canViewInventory" className="text-sm">View Inventory</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditInventory" 
                          checked={formData.permissions.canEditInventory}
                          onCheckedChange={() => togglePermission('canEditInventory')}
                        />
                        <label htmlFor="canEditInventory" className="text-sm">Edit Inventory</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewReports" 
                          checked={formData.permissions.canViewReports}
                          onCheckedChange={() => togglePermission('canViewReports')}
                        />
                        <label htmlFor="canViewReports" className="text-sm">View Reports</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Accounts (Income/Expenses) */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Accounts - Income/Expenses</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewAccounts" 
                          checked={formData.permissions.canViewAccounts}
                          onCheckedChange={() => togglePermission('canViewAccounts')}
                        />
                        <label htmlFor="canViewAccounts" className="text-sm">View Income & Expenses</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditAccounts" 
                          checked={formData.permissions.canEditAccounts}
                          onCheckedChange={() => togglePermission('canEditAccounts')}
                        />
                        <label htmlFor="canEditAccounts" className="text-sm">Edit Income & Expenses</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Account Heads */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Accounts - Heads</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewAccountHeads" 
                          checked={formData.permissions.canViewAccountHeads}
                          onCheckedChange={() => togglePermission('canViewAccountHeads')}
                        />
                        <label htmlFor="canViewAccountHeads" className="text-sm">View Account Heads</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditAccountHeads" 
                          checked={formData.permissions.canEditAccountHeads}
                          onCheckedChange={() => togglePermission('canEditAccountHeads')}
                        />
                        <label htmlFor="canEditAccountHeads" className="text-sm">Edit Account Heads</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Transfers (Contra) */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Accounts - Transfers</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewTransfers" 
                          checked={formData.permissions.canViewTransfers}
                          onCheckedChange={() => togglePermission('canViewTransfers')}
                        />
                        <label htmlFor="canViewTransfers" className="text-sm">View Transfers (Contra)</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditTransfers" 
                          checked={formData.permissions.canEditTransfers}
                          onCheckedChange={() => togglePermission('canEditTransfers')}
                        />
                        <label htmlFor="canEditTransfers" className="text-sm">Edit Transfers (Contra)</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Account Reports */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Accounts - Reports</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewAccountReports" 
                          checked={formData.permissions.canViewAccountReports}
                          onCheckedChange={() => togglePermission('canViewAccountReports')}
                        />
                        <label htmlFor="canViewAccountReports" className="text-sm">View Account Reports</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invoices */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Invoices</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canViewInvoices" 
                          checked={formData.permissions.canViewInvoices}
                          onCheckedChange={() => togglePermission('canViewInvoices')}
                        />
                        <label htmlFor="canViewInvoices" className="text-sm">View Invoices</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canEditInvoices" 
                          checked={formData.permissions.canEditInvoices}
                          onCheckedChange={() => togglePermission('canEditInvoices')}
                        />
                        <label htmlFor="canEditInvoices" className="text-sm">Create / Edit / Delete Invoices</label>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Analytics Reports */}
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Analytics Reports Download</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canExportBookingAnalytics" 
                          checked={formData.permissions.canExportBookingAnalytics}
                          onCheckedChange={() => togglePermission('canExportBookingAnalytics')}
                        />
                        <label htmlFor="canExportBookingAnalytics" className="text-sm">Booking Analytics Report</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="canExportFinancialAnalytics" 
                          checked={formData.permissions.canExportFinancialAnalytics}
                          onCheckedChange={() => togglePermission('canExportFinancialAnalytics')}
                        />
                        <label htmlFor="canExportFinancialAnalytics" className="text-sm">Financial Analytics Report</label>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Financial Visibility - Only for Manager and Stakeholder */}
              {(formData.role === 'manager' || formData.role === 'stakeholder') && (
                <div className="space-y-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-900 dark:text-foreground flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Financial Visibility
                  </h3>
                  <Card className={`border-2 ${formData.hideFinancials ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-green-300 bg-green-50 dark:bg-green-900/20'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            {formData.hideFinancials ? (
                              <EyeOff className="w-5 h-5 text-red-600" />
                            ) : (
                              <Eye className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-medium">
                              {formData.hideFinancials ? 'Financial Information Hidden' : 'Financial Information Visible'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
                            {formData.hideFinancials 
                              ? 'This team member cannot see any financial data (amounts, payments, revenue)' 
                              : 'This team member can see all financial information'}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant={formData.hideFinancials ? "default" : "outline"}
                          className={formData.hideFinancials ? "bg-red-600 hover:bg-red-700" : ""}
                          onClick={() => setFormData({...formData, hideFinancials: !formData.hideFinancials})}
                        >
                          {formData.hideFinancials ? 'Allow Financials' : 'Hide Financials'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Notification Preferences */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-foreground flex items-center">
                  <Bell className="w-4 h-4 mr-2" />
                  Notification Preferences
                </h3>
                <p className="text-sm text-gray-600 dark:text-muted-foreground">
                  Choose which notifications this user should receive
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Payment & Financial</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="paymentReminders" 
                          checked={formData.notificationPreferences.paymentReminders}
                          onCheckedChange={() => toggleNotificationPreference('paymentReminders')}
                        />
                        <label htmlFor="paymentReminders" className="text-sm">Payment Reminders</label>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm dark:text-foreground">Bookings & Leads</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="newBookings" 
                          checked={formData.notificationPreferences.newBookings}
                          onCheckedChange={() => toggleNotificationPreference('newBookings')}
                        />
                        <label htmlFor="newBookings" className="text-sm">New Booking Alerts</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="bookingUpdates" 
                          checked={formData.notificationPreferences.bookingUpdates}
                          onCheckedChange={() => toggleNotificationPreference('bookingUpdates')}
                        />
                        <label htmlFor="bookingUpdates" className="text-sm">Booking Updates</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="newLeads" 
                          checked={formData.notificationPreferences.newLeads}
                          onCheckedChange={() => toggleNotificationPreference('newLeads')}
                        />
                        <label htmlFor="newLeads" className="text-sm">New Lead Alerts</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="leadUpdates" 
                          checked={formData.notificationPreferences.leadUpdates}
                          onCheckedChange={() => toggleNotificationPreference('leadUpdates')}
                        />
                        <label htmlFor="leadUpdates" className="text-sm">Lead Updates</label>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
                  {editingUser ? 'Update' : 'Create'} Staff Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-3 sm:p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
            <Input
              placeholder="Search staff members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Staff List */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <Card key={user.id} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-3 sm:p-6">
                <div className="flex flex-col gap-3">
                  {/* User Info Row */}
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg flex-shrink-0 ${
                      user.role === 'manager' ? 'bg-gradient-to-br from-orange-500 to-red-600 text-white' :
                      'bg-gradient-to-br from-blue-500 to-cyan-600 text-white'
                    }`}>
                      {user.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-foreground truncate">{user.name}</h3>
                        <Badge className={`${getRoleBadgeColor(user.role)} text-[10px] sm:text-xs`}>
                          {(user.role || 'staff').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="space-y-0.5">
                        <div className="flex items-center text-xs sm:text-sm text-gray-700 dark:text-foreground">
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center text-xs sm:text-sm text-gray-700 dark:text-foreground">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Permissions Preview */}
                  {user.permissions && (
                    <div className="flex flex-wrap gap-1">
                      {user.permissions.canEditBookings && <Badge variant="outline" className="text-[10px]">Edit Bookings</Badge>}
                      {user.permissions.canEditLeads && <Badge variant="outline" className="text-[10px]">Edit Leads</Badge>}
                      {user.permissions.canEditVenues && <Badge variant="outline" className="text-[10px]">Edit Venues</Badge>}
                      {user.permissions.canViewReports && <Badge variant="outline" className="text-[10px]">View Reports</Badge>}
                    </div>
                  )}

                  {/* Financial Visibility Status */}
                  {(user.role === 'manager' || user.role === 'stakeholder' || user.role === 'staff') && (
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium w-fit ${
                      user.hideFinancials 
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 border border-red-200' 
                        : 'bg-green-100 dark:bg-green-900/30 text-green-800 border border-green-200'
                    }`}>
                      {user.hideFinancials ? (
                        <><EyeOff className="w-3 h-3 mr-1" /> Financials Hidden</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1" /> Financials Visible</>
                      )}
                    </div>
                  )}

                  {/* Action Buttons - Grid on mobile */}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    {/* Financial Toggle Button */}
                    {(user.role === 'manager' || user.role === 'stakeholder' || user.role === 'staff') && (
                      <Button
                        onClick={() => handleToggleStaffFinancials(user)}
                        variant="outline"
                        size="sm"
                        className={`h-9 text-xs ${user.hideFinancials 
                          ? "hover:bg-green-50 dark:bg-green-900/20 hover:border-green-300 hover:text-green-700" 
                          : "hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700"
                        }`}
                      >
                        {user.hideFinancials ? (
                          <><Eye className="w-3.5 h-3.5 mr-1" /> Show $</>
                        ) : (
                          <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide $</>
                        )}
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEdit(user)}
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs hover:bg-teal-50 dark:bg-teal-900/20 hover:border-teal-300 hover:text-teal-700"
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDeleteClick(user)}
                      variant="outline"
                      size="sm"
                      className="h-9 text-xs hover:bg-red-50 dark:bg-red-900/20 hover:border-red-300 hover:text-red-700"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </div>

                  <p className="text-[10px] text-gray-400 dark:text-muted-foreground">
                    Created: {formatDateTime(user.createdAt)}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="border-0 shadow-md">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 dark:text-muted-foreground mx-auto mb-4" />
              <p className="text-gray-600 dark:text-muted-foreground text-lg">No staff members found</p>
              <p className="text-gray-500 dark:text-muted-foreground text-sm mt-2">Add your first staff member to get started</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${userToDelete?.role === 'admin' ? 'Admin' : 'Staff Member'}?`}
        description="This will soft delete this user from the system. The user will be marked as deleted but data will be retained for audit purposes."
        itemName={userToDelete ? `${userToDelete.name} (${userToDelete.email})` : ''}
        confirmText="Proceed to Verification"
      />

      {/* Password Verification Dialog */}
      <PasswordVerificationDialog
        open={passwordVerifyOpen}
        onOpenChange={setPasswordVerifyOpen}
        onVerify={handlePasswordVerify}
        title="Verify Your Password"
        description="Please enter your password to confirm the deletion of this user."
        userEmail={currentUser?.email}
      />
    </div>
  );
};

export default StaffManagement;
